import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Description as BillsIcon, Receipt, FolderOpen, EditNote, CurrencyRupeeOutlined, AccountBalanceWallet } from "@mui/icons-material";
import { Button, Card, Box } from "@mui/material";
import {
    Add as AddIcon,
    Search as SearchIcon,
    ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import "./BillsList.css";

export default function BillsList() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [bills, setBills] = useState([]);
    const [selectedBills, setSelectedBills] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("All statuses");
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Load bills from localStorage
    useEffect(() => {
        const storedBills =
            JSON.parse(localStorage.getItem("bills")) || [];

        setBills(storedBills);
    }, []);


    // uploaded file

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);

        if (files.length === 0) return;

        console.log("Selected files:", files);

        // Example: save file information
        const uploadedFiles = files.map((file) => ({
            name: file.name,
            size: file.size,
            type: file.type,
            uploadedAt: new Date().toISOString(),
        }));

        localStorage.setItem(
            "uploadedBills",
            JSON.stringify(uploadedFiles)
        );

        alert(`${files.length} file(s) uploaded successfully`);

        // Reset input so the same file can be selected again
        e.target.value = "";
    };
    // Select / Unselect all bills
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedBills(bills.map((bill) => bill.id));
        } else {
            setSelectedBills([]);
        }
    };

    // Select / Unselect one bill
    const handleSelectBill = (id) => {
        setSelectedBills((prev) => {
            if (prev.includes(id)) {
                return prev.filter((billId) => billId !== id);
            }

            return [...prev, id];
        });
    };

    // Delete bill
    const handleDelete = (id) => {
        const confirmDelete = window.confirm(
            "Are you sure you want to delete this bill?"
        );

        if (!confirmDelete) return;

        const updatedBills = bills.filter(
            (bill) => bill.id !== id
        );

        localStorage.setItem(
            "bills",
            JSON.stringify(updatedBills)
        );

        setBills(updatedBills);

        // Remove from selected bills also
        setSelectedBills((prev) =>
            prev.filter((billId) => billId !== id)
        );
    };

    // View bill
    const handleView = (bill) => {
        alert(
            `Bill: ${bill.billNumber}\nVendor: ${bill.vendorName}\nAmount: ₹${bill.amount}`
        );
    };

    // ================= EDIT BILL =================
    const handleEdit = (bill) => {
        navigate("/dashboard/new-bill", {
            state: {
                bill: bill,
                isEdit: true
            }
        });
    };

    const filteredBills = bills.filter((bill) => {
        const query = searchQuery.trim().toLowerCase();
        const matchesSearch = !query || [
            bill.billNumber,
            bill.referenceNumber,
            bill.vendorName,
            bill.status,
        ].some((value) => String(value || "").toLowerCase().includes(query));

        const matchesStatus =
            statusFilter === "All statuses" ||
            String(bill.status || "").toLowerCase() === statusFilter.toLowerCase();

        return matchesSearch && matchesStatus;
    });

    const isAllSelected =
        filteredBills.length > 0 &&
        filteredBills.every((bill) => selectedBills.includes(bill.id));

    return (
        <div className="bills-page">

            {/* ================= HEADER ================= */}
            <div className="bills-page-header">

                <div className="bills-header-left">
                    <div className="bills-page-icon">
                        <BillsIcon />
                    </div>
                    <div className="bills-page-title-wrap">
                        <h1 className="bills-title">Bills</h1>
                        <p className="bills-subtitle">Track and manage bills from your vendors.</p>
                    </div>
                </div>

                <div className="bills-header-actions">
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: "none" }}
                        onChange={handleFileUpload}
                    />

                    <Button
                        className="bills-upload-btn"
                        variant="outlined"
                        onClick={() => fileInputRef.current.click()}
                    >
                        Upload Bills
                    </Button>

                    <Button
                        className="bills-primary-btn"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/dashboard/new-bill")}
                    >
                        New Bill
                    </Button>
                </div>

            </div>

            {/* ================= SUMMARY CARDS ================= */}

                <div className="bills-summary-cards">

                    {/* Total Bills */}
                    <Card className="bill-summary-card bill-summary-card-total">
                        <Box className="summary-card-icon"><Receipt /></Box>
                        <Box className="summary-card-content">
                            <span className="summary-card-label">
                                Total Bills
                            </span>

                            <h3>
                                {bills.length}
                            </h3>
                            <small>All bills</small>
                        </Box>
                    </Card>

                    {/* Open Bills */}
                    <Card className="bill-summary-card bill-summary-card-open">
                        <Box className="summary-card-icon"><FolderOpen /></Box>
                        <Box className="summary-card-content">
                            <span className="summary-card-label">
                                Open Bills
                            </span>

                            <h3>
                                {
                                    bills.filter(
                                        (bill) => bill.status === "Open"
                                    ).length
                                }
                            </h3>
                            <small>Bills currently open</small>
                        </Box>
                    </Card>

                    {/* Draft Bills */}
                    <Card className="bill-summary-card bill-summary-card-draft">
                        <Box className="summary-card-icon"><EditNote /></Box>
                        <Box className="summary-card-content">
                            <span className="summary-card-label">
                                Draft Bills
                            </span>

                            <h3>
                                {
                                    bills.filter(
                                        (bill) => bill.status === "Draft"
                                    ).length
                                }
                            </h3>
                            <small>Bills in draft</small>
                        </Box>
                    </Card>

                    {/* Total Amount */}
                    <Card className="bill-summary-card bill-summary-card-amount">
                        <Box className="summary-card-icon"><CurrencyRupeeOutlined /></Box>
                        <Box className="summary-card-content">
                            <span className="summary-card-label">
                                Total Amount
                            </span>

                            <h3>
                                ₹{" "}
                                {bills
                                    .reduce(
                                        (total, bill) =>
                                            total +
                                            Number(bill.amount || 0),
                                        0
                                    )
                                    .toFixed(2)}
                            </h3>
                            <small>Total bill amount</small>
                        </Box>
                    </Card>

                    {/* Balance Due */}
                    <Card className="bill-summary-card bill-summary-card-balance">
                        <Box className="summary-card-icon"><AccountBalanceWallet /></Box>
                        <Box className="summary-card-content">
                            <span className="summary-card-label">
                                Balance Due
                            </span>

                            <h3>
                                ₹{" "}
                                {bills
                                    .reduce(
                                        (total, bill) =>
                                            total +
                                            Number(bill.balanceDue || 0),
                                        0
                                    )
                                    .toFixed(2)}
                            </h3>
                            <small>Pending payment amount</small>
                        </Box>
                    </Card>

                </div>



            {/* ================= FILTERS ================= */}
            {!filtersOpen ? (
                <button
                    type="button"
                    className="bills-show-filters-btn"
                    onClick={() => setFiltersOpen(true)}
                >
                    <SearchIcon className="bills-show-filters-icon" />
                    <span>Show Filters</span>
                </button>
            ) : (
                <div className="bills-filter-card">
                    <div className="bills-filter-header">
                        <button
                            type="button"
                            className="bills-filter-title-btn"
                            onClick={() => setFiltersOpen(false)}
                            aria-label="Hide filters"
                            title="Hide filters"
                        >
                            <span className="bills-filter-icon" aria-hidden="true">
                                <SearchIcon />
                            </span>
                            <span className="bills-filter-title">Filters</span>
                            <ExpandLessIcon className="bills-filter-collapse-icon" />
                        </button>

                        <button
                            type="button"
                            className="bills-filter-clear"
                            onClick={() => {
                                setSearchQuery("");
                                setStatusFilter("All statuses");
                            }}
                            disabled={!searchQuery && statusFilter === "All statuses"}
                        >
                            Clear Filters
                        </button>
                    </div>

                    <div className="bills-filter-controls">
                        <div className="bills-filter-search-wrap">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(event) => setSearchQuery(event.target.value)}
                                placeholder="Search bill number, reference number or vendor"
                                className="bills-filter-search"
                                aria-label="Search bills"
                            />
                        </div>

                        <select
                            value={statusFilter}
                            onChange={(event) => setStatusFilter(event.target.value)}
                            className="bills-status-filter"
                            aria-label="Filter bills by status"
                        >
                            <option>All statuses</option>
                            <option>Open</option>
                            <option>Draft</option>
                        </select>

                        <span className="bills-filter-count">
                            {filteredBills.length} {filteredBills.length === 1 ? "bill" : "bills"}
                        </span>
                    </div>
                </div>
            )}

            {/* ================= TABLE CARD ================= */}
            <div className="bills-card">

                {/* ================= TABLE ================= */}
                {/* <div className="bills-table-wrapper"></div> */}

                {/* ================= TABLE ================= */}
                <div className="bills-table-wrapper">

                    <table className="bills-table">

                        <thead>
                            <tr>

                                <th className="checkbox-column">
                                    <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        aria-label="Select all bills"
                                    />
                                </th>

                                <th>DATE</th>
                                <th>BILL#</th>
                                <th>REFERENCE NUMBER</th>
                                <th>VENDOR NAME</th>
                                <th>STATUS</th>
                                <th>DUE DATE</th>

                                <th className="amount-column">
                                    AMOUNT
                                </th>

                                <th className="amount-column">
                                    BALANCE DUE
                                </th>

                                <th>ACTION</th>

                            </tr>
                        </thead>

                        <tbody>

                            {filteredBills.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="10"
                                        className="empty-bills"
                                    >

                                        <div className="empty-icon">
                                            📄
                                        </div>

                                        <h3>
                                            {bills.length === 0 ? "No Bills Found" : "No Matching Bills"}
                                        </h3>

                                        <p>
                                            {bills.length === 0
                                                ? <>Create your first bill by clicking the <strong>+ New</strong> button.</>
                                                : "Try changing your search or status filter."}
                                        </p>

                                        {bills.length === 0 ? (
                                            <button
                                                className="new-bill-btn"
                                                type="button"
                                                onClick={() => navigate("/dashboard/new-bill")}
                                            >
                                                + New Bill
                                            </button>
                                        ) : (
                                            <button
                                                className="new-bill-btn"
                                                type="button"
                                                onClick={() => {
                                                    setSearchQuery("");
                                                    setStatusFilter("All statuses");
                                                }}
                                            >
                                                Clear Filters
                                            </button>
                                        )}

                                    </td>

                                </tr>

                            ) : (

                                filteredBills.map((bill) => (

                                    <tr key={bill.id}>

                                        {/* CHECKBOX */}
                                        <td className="checkbox-column">

                                            <input
                                                type="checkbox"
                                                checked={selectedBills.includes(
                                                    bill.id
                                                )}
                                                onChange={() =>
                                                    handleSelectBill(
                                                        bill.id
                                                    )
                                                }
                                                aria-label={`Select bill ${bill.billNumber || ""}`}
                                            />

                                        </td>

                                        {/* DATE */}
                                        <td>
                                            {bill.date || "-"}
                                        </td>

                                        {/* BILL NUMBER */}
                                        <td>
                                            {bill.billNumber || "-"}
                                        </td>

                                        {/* REFERENCE NUMBER */}
                                        <td>
                                            {bill.referenceNumber || "-"}
                                        </td>

                                        {/* VENDOR */}
                                        <td>

                                            <div className="vendor-cell">

                                                <div className="vendor-avatar">
                                                    {bill.vendorName
                                                        ? bill.vendorName
                                                            .charAt(0)
                                                            .toUpperCase()
                                                        : "V"}
                                                </div>

                                                <span>
                                                    {bill.vendorName || "-"}
                                                </span>

                                            </div>

                                        </td>

                                        {/* STATUS */}
                                        <td>

                                            <span
                                                className={`status-badge ${bill.status === "Open"
                                                    ? "status-open"
                                                    : "status-draft"
                                                    }`}
                                            >
                                                {bill.status || "-"}
                                            </span>

                                        </td>

                                        {/* DUE DATE */}
                                        <td>
                                            {bill.dueDate || "-"}
                                        </td>

                                        {/* AMOUNT */}
                                        <td className="amount-column">

                                            ₹{" "}
                                            {Number(
                                                bill.amount || 0
                                            ).toFixed(2)}

                                        </td>

                                        {/* BALANCE DUE */}
                                        <td className="amount-column">

                                            ₹{" "}
                                            {Number(
                                                bill.balanceDue || 0
                                            ).toFixed(2)}

                                        </td>

                                        {/* ACTIONS */}
                                        <td>

                                            <div className="action-buttons">

                                                {/* VIEW */}
                                                <button
                                                    className="view-btn"
                                                    title="View"
                                                    onClick={() =>
                                                        handleView(bill)
                                                    }
                                                >
                                                    👁
                                                </button>

                                                {/* EDIT */}
                                                <button
                                                    className="edit-btn"
                                                    title="Edit"
                                                    onClick={() =>
                                                        handleEdit(bill)
                                                    }
                                                >
                                                    ✏
                                                </button>

                                                {/* DELETE */}
                                                <button
                                                    className="delete-btn"
                                                    title="Delete"
                                                    onClick={() =>
                                                        handleDelete(
                                                            bill.id
                                                        )
                                                    }
                                                >
                                                    🗑
                                                </button>

                                            </div>

                                        </td>

                                    </tr>

                                ))

                            )}

                        </tbody>

                    </table>

                </div>

            </div>

        </div>
    );
}